import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api, aoPerderSessao, token, tokenDoAtor } from './api';
import { esquecerEsteAparelho } from './push';

/**
 * Sessão e permissões.
 *
 * As permissões vindas de `/auth/eu` servem para ESCONDER o que o usuário
 * não pode fazer — nunca para autorizar. A autorização acontece no servidor,
 * em toda rota, e é resolvida pela função `pode()` do banco (RNF-02). Se um
 * dia esta lista mentir, o pior que acontece é um botão aparecer e a API
 * recusar com uma mensagem clara.
 */

export interface Eu {
  usuarioId: string;
  perfil: string;
  /** Nome do funcionário; cai para o e-mail quando o usuário não tem ficha. */
  nome: string | null;
  /**
   * A ficha de funcionário desta conta, quando existe.
   *
   * É o que deixa a tela marcar sozinha quem recebeu, quem executou, quem
   * elaborou — sempre pelo `id`, nunca pelo nome, porque o cadastro tem
   * nomes repetidos. Nulo em conta sem ficha (administrador de sistema):
   * aí a tela não marca ninguém, e quem não é funcionário não assina.
   */
  funcionarioId: string | null;
  /**
   * Quem está VESTINDO esta sessão, quando ela é um «Entrar como».
   *
   * Nulo na sessão normal. Preenchido, é o que faz a faixa do topo
   * aparecer — e a faixa é o que impede alguém de esquecer que está
   * atuando como outro perfil e lançar ensaio achando que é ele mesmo.
   */
  atuandoPor: string | null;
  /** O nome dessa pessoa, para a faixa dizer quem é «você». */
  atorNome: string | null;
  permissoes: string[];
  /**
   * Os espaços de trabalho que a pessoa alcança — `hub`, `labmanager`,
   * `campo` —, na ordem da faixa (onda 295, spec 03 §9).
   *
   * Calculados no servidor, a partir das permissões e da unidade de acesso
   * a que cada uma pertence (`vw_usuario_espaco`); `comum` e `sistema` nunca
   * entram. A web não deduz mais o alcance pelo prefixo das permissões: o
   * que a faixa oferece, a porta e a recusa saem daqui
   * (`espacosDaSessao`, em `modulos.ts`). Como as permissões, serve para
   * ESCONDER e explicar — a recusa de verdade é a da API.
   */
  espacos: string[];
  /**
   * 0211 (RN-214): a porta da exclusão definitiva. Verdadeiro só para a
   * conta com a marca, na instalação que a liga, sem persona vestida.
   */
  exclusaoDefinitiva?: boolean;
  /**
   * 0268 (RN-265): os superpoderes da homologação — a conta com a marca
   * lança, verifica e aprova o próprio trabalho. Só destrava os botões; a
   * recusa (ou a passagem) de verdade é a do banco.
   */
  superpoderes?: boolean;
}

interface Contexto {
  eu: Eu | null;
  carregando: boolean;
  entrar(usuario: string, senha: string): Promise<void>;
  sair(): void;
  pode(permissao: string): boolean;
  /** Se a pessoa alcança o espaço de trabalho (`hub`, `labmanager`, `campo`). */
  temEspaco(espaco: string): boolean;
  /** Vestir uma persona de demonstração (0079). Só o admin enxerga isto. */
  entrarComo(personaId: string): Promise<void>;
  /** Voltar a ser você. Usa o token guardado; não pede nada ao servidor. */
  voltarASerEu(): Promise<void>;
}

const Ctx = createContext<Contexto | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [eu, setEu] = useState<Eu | null>(null);
  const [carregando, setCarregando] = useState(true);

  /*
   * Perder a sessão (o /auth/eu da abertura que falha — token vencido, sem
   * rede no galpão —, o «Voltar a ser eu» que não volta) só encerra a
   * sessão: não mexe no push (revisão da onda 548, N-1). A pessoa continua
   * recebendo os avisos no aparelho até ela mesma sair.
   */
  const encerrar = useCallback(() => {
    token.limpar();
    setEu(null);
  }, []);

  /* O «Sair» do botão — a saída VOLUNTÁRIA —, e só ele, desfaz a inscrição
     no push deste navegador (I-3), com o token de quem sai e sem segurar a
     saída. */
  const sair = useCallback(() => {
    void esquecerEsteAparelho(token.ler());
    encerrar();
  }, [encerrar]);

  // Token no armazenamento não prova sessão válida: pode ter expirado com o
  // celular no bolso. Quem diz se ela vale é o servidor.
  useEffect(() => {
    let vivo = true;
    if (!token.ler()) { setCarregando(false); return; }
    api<Eu>('/auth/eu')
      .then((r) => { if (vivo) setEu(r); })
      .catch(() => { if (vivo) encerrar(); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [encerrar]);

  // Qualquer 401 vindo de qualquer tela derruba a sessão aqui, num lugar só.
  useEffect(() => aoPerderSessao(() => setEu(null)), []);

  const entrar = useCallback(async (usuario: string, senha: string) => {
    const r = await api<{ token: string }>('/auth/entrar', {
      /*
       * `usuario` aceita login OU e-mail desde a 0052. O campo antigo
       * `email` continua funcionando na API — nenhum cliente parado numa
       * versão velha fica de fora — mas quem manda daqui manda o novo.
       */
      corpo: { usuario, senha },
    });
    token.gravar(r.token);
    setEu(await api<Eu>('/auth/eu'));
  }, []);

  /**
   * Vestir a persona.
   *
   * A ordem importa: guarda o token próprio ANTES de trocar. Trocar
   * primeiro e guardar depois deixaria, numa falha no meio, uma sessão
   * de persona sem caminho de volta — e a saída seria sair e entrar de
   * novo, que é exatamente o que este recurso existe para evitar.
   */
  const entrarComo = useCallback(async (personaId: string) => {
    const meu = token.ler();
    if (!meu) throw new Error('Sessão ausente.');
    const r = await api<{ token: string }>(
      `/auth/entrar-como/${personaId}`, { metodo: 'POST' });
    tokenDoAtor.gravar(meu);
    token.gravar(r.token);
    setEu(await api<Eu>('/auth/eu'));
  }, []);

  /**
   * Voltar a ser você.
   *
   * Local, sem ida ao servidor: o token próprio nunca saiu do navegador.
   * Se ele tiver expirado enquanto a persona estava vestida, o `/auth/eu`
   * devolve 401 e a sessão cai — que é o comportamento certo, e o mesmo
   * de qualquer token vencido.
   */
  const voltarASerEu = useCallback(async () => {
    const meu = tokenDoAtor.ler();
    if (!meu) { encerrar(); return; }
    token.gravar(meu);
    tokenDoAtor.limpar();
    try {
      setEu(await api<Eu>('/auth/eu'));
    } catch {
      encerrar();
    }
  }, [encerrar]);

  const valor = useMemo<Contexto>(() => ({
    eu, carregando, entrar, sair, entrarComo, voltarASerEu,
    pode: (p: string) => !!eu?.permissoes.includes(p),
    temEspaco: (e: string) => (eu?.espacos ?? []).includes(e),
  }), [eu, carregando, entrar, sair, entrarComo, voltarASerEu]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSessao(): Contexto {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSessao fora do ProvedorSessao');
  return c;
}

/** Rota que exige sessão. Guarda de onde o usuário veio para voltar depois. */
export function ExigeSessao({ children }: { children: ReactNode }) {
  const { eu, carregando } = useSessao();
  const local = useLocation();
  if (carregando) return <span className="carregando" role="status" aria-label="Carregando" />;
  if (!eu) return <Navigate to="/entrar" replace state={{ de: local.pathname + local.search }} />;
  return <>{children}</>;
}
